import styled from 'styled-components';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Flex } from 'foundations-components/components/Flex';
import ColumnIconV2 from './ColumnIconV2';
import FormattedNumber from 'I18n/components/FormattedNumber';
import isNumber from 'transmute/isNumber';
import { useCallback, useMemo } from 'react';
import { ExpandCollapseIcon } from './ExpandCollapseIcon';
import { useConfigs } from '../lib/config-context';
import { HeaderFooterWrapper } from './HeaderFooterWrapper';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const COLUMN_HEADER_HEIGHT = 36;
const ARROW_WIDTH = 12;
const StyledHeaderSummary = styled.div.withConfig({
  displayName: "ColumnHeaderV2__StyledHeaderSummary",
  componentId: "sc-1494ujy-0"
})(({
  theme,
  columnBackgroundColor,
  collapsed
}) => `
  text-align: left;
  border-top: 1px solid ${theme.color['border-core-subtle']};
  border-bottom: ${collapsed ? '0px' : '1px'} solid ${theme.color['border-core-subtle']};

  background-color: ${theme.color[columnBackgroundColor]};
  font-size: ${theme.typography['heading-25'].fontSize};
  line-height: ${theme.typography['heading-25'].lineHeight};
  font-weight: ${theme.typography['heading-25'].fontWeight};
  letter-spacing: ${theme.typography['heading-25'].letterSpacing};
  font-family: ${theme.typography['heading-25'].fontFamily};
  position: relative;
  padding-left: ${theme.space[collapsed ? '200' : '400']};
  padding-right: ${theme.space[collapsed ? '0' : '200']};
  height: ${COLUMN_HEADER_HEIGHT}px;
  display: flex;
  align-items: center;
  width:  ${collapsed ? 'calc(100% - 1px)' : '100%'};


.cdb-column-header-left-arrow-container{
  width: ${ARROW_WIDTH}px; // plus one for stroke
  height: ${collapsed ? COLUMN_HEADER_HEIGHT + 2 : COLUMN_HEADER_HEIGHT}px;
  position:absolute;
  left:0px;
  overflow:hidden;
}
.cdb-column-header-left-arrow {
  width: ${COLUMN_HEADER_HEIGHT + 1}px; // plus one for stroke
  height: ${COLUMN_HEADER_HEIGHT + 1}px;
  position:absolute;
  right:${collapsed ? '2px' : '3px'};
  top:${collapsed ? '-2px' : '-3px'};

  transform: rotate(-60deg) skew(-32deg);
  background-color: ${theme.color['fill-surface-default']};
  border:1px solid ${theme.color['border-core-subtle']};

}

.cdb-column-header-right-arrow-container{
 width: ${ARROW_WIDTH}px; // plus one for stroke
  height: ${collapsed ? COLUMN_HEADER_HEIGHT + 1 : COLUMN_HEADER_HEIGHT}px;
  position:absolute;
  right:-${ARROW_WIDTH}px;
  overflow:hidden;
}
.cdb-column-header-right-arrow {
  width: ${COLUMN_HEADER_HEIGHT + 1}px; // plus one for stroke
  height: ${COLUMN_HEADER_HEIGHT + 1}px;
  position:absolute;
  right:${collapsed ? '2px' : '3px'};
  top:${collapsed ? '-2px' : '-3px'};

  transform: rotate(-60deg) skew(-32deg);
  background-color: ${theme.color[columnBackgroundColor]};
  border:1px solid ${theme.color['border-core-subtle']};

}


  /* First column header - straight left edge */
  [data-test-id="cdb-columns-container"] > *:first-child & {
    border-left:1px solid ${theme.color['border-core-subtle']};
    border-top-left-radius: ${theme.borderRadius['100']};
    .cdb-column-header-left-arrow-container {
      display: none;
    }
  }

  /* Last column header - straight right edge */
  [data-test-id="cdb-columns-container"] > *:last-child & {
    width:  100%;
    border-right:1px solid ${theme.color['border-core-subtle']};
    border-top-right-radius: ${theme.borderRadius['100']};
    .cdb-column-header-right-arrow-container {
      display: none;
    }
  }
`);
export const ColumnHeaderV2 = ({
  columnData,
  onHovered,
  columnBackgroundColor,
  onExpandCollapse,
  isBoardEmpty
}) => {
  const {
    name,
    totalCards,
    collapsed,
    id
  } = columnData;
  const {
    renderHeaderSummary
  } = useConfigs();
  const shouldRenderHeaderSummary = !collapsed && !isBoardEmpty && renderHeaderSummary;
  const handleExpandCollapse = useCallback(() => {
    if (!onExpandCollapse) {
      return undefined;
    }
    onExpandCollapse(id, !collapsed, columnData);
  }, [collapsed, columnData, id, onExpandCollapse]);
  const shouldCollapse = useMemo(() => {
    if (!onExpandCollapse) {
      return false;
    }
    return Boolean(collapsed);
  }, [collapsed, onExpandCollapse]);

  //one for each arrow class
  //@bend-expect trellis-tokenized
  //@bend-expect trellis-tokenized
  //@bend-expect trellis-tokenized
  //@bend-expect trellis-tokenized
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(StyledHeaderSummary, {
      "data-test-id": "cdb-column-header",
      columnBackgroundColor: columnBackgroundColor,
      collapsed: shouldCollapse,
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("div", {
          className: "cdb-column-header-left-arrow-container",
          "data-test-id": "cdb-column-header-left-arrow-container",
          children: /*#__PURE__*/_jsx("div", {
            className: "cdb-column-header-left-arrow"
          })
        }), shouldCollapse ? /*#__PURE__*/_jsx(ExpandCollapseIcon, {
          marginInlineStart: "100",
          collapsed: shouldCollapse,
          onHovered: onHovered,
          onExpandCollapse: handleExpandCollapse
        }) : /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "row",
          alignItems: "center",
          inlineSize: "100%",
          gap: "200",
          children: [/*#__PURE__*/_jsx(TruncateString, {
            useFlex: true,
            fixedChildren: /*#__PURE__*/_jsxs(Flex, {
              as: "span",
              paddingInlineStart: "200",
              alignItems: "center",
              gap: "200",
              children: [isNumber(totalCards) && /*#__PURE__*/_jsx(FormattedNumber, {
                value: totalCards,
                "data-test-id": "cdb-column-total-cards"
              }), /*#__PURE__*/_jsx(ColumnIconV2, {
                columnData: columnData
              })]
            }),
            children: /*#__PURE__*/_jsx("span", {
              "data-test-id": "cdb-column-name",
              children: name
            })
          }), onExpandCollapse && /*#__PURE__*/_jsx(View, {
            marginInlineStart: "auto",
            children: /*#__PURE__*/_jsx(ExpandCollapseIcon, {
              collapsed: shouldCollapse,
              onHovered: onHovered,
              onExpandCollapse: handleExpandCollapse
            })
          })]
        }), /*#__PURE__*/_jsx("div", {
          className: "cdb-column-header-right-arrow-container",
          "data-test-id": "cdb-column-header-right-arrow-container",
          children: /*#__PURE__*/_jsx("div", {
            className: "cdb-column-header-right-arrow"
          })
        })]
      })
    }), shouldRenderHeaderSummary && /*#__PURE__*/_jsx(HeaderFooterWrapper, {
      borderStyleProps: {
        borderBlockEndColor: 'border-core-subtle',
        borderBlockEndStyle: 'solid',
        borderBlockEndWidth: '100'
      },
      children: renderHeaderSummary(columnData)
    })]
  });
};